'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IconAlertTriangle, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { formatFailedSummary } from '@/lib/projects/source-status';
import { formatRelativeTime } from '@/lib/projects/project-utils';

interface PartialFailureBannerProps {
  failed: SourceRunStatus[];
  onDismiss?: () => void;
}

export function PartialFailureBanner({ failed, onDismiss }: PartialFailureBannerProps) {
  const [expanded, setExpanded] = useState(false);

  if (failed.length === 0) return null;

  return (
    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 mb-4">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <IconAlertTriangle className="size-4 text-yellow-600 shrink-0" />
          <p className="text-sm text-foreground">
            {formatFailedSummary(failed)}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <>
                Hide <IconChevronUp className="size-3 ml-1" />
              </>
            ) : (
              <>
                Details <IconChevronDown className="size-3 ml-1" />
              </>
            )}
          </Button>
          {onDismiss && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <IconX className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-yellow-500/20 px-4 py-3 space-y-2">
          {failed.map((source) => (
            <div
              key={source.sourceId}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium">{source.name}</span>
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                    Error
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{source.domain}</p>
                {source.message && (
                  <p className="text-xs text-muted-foreground mt-0.5">{source.message}</p>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatRelativeTime(source.lastRunAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
